import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker, DayProps } from "react-day-picker"

import { cn } from "../../lib/utils"
import { buttonVariants } from "../ui/button"
import { format } from 'date-fns';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  workoutCounts?: Record<string, number>;
  selected?: Date;
};

interface ExtendedDayProps extends DayProps {
  modifiers?: Record<string, boolean>;
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  workoutCounts = {},
  selected,
  ...props
}: CalendarProps) {
  const workoutDays = Object.keys(workoutCounts).reduce<Record<string, { count: number, marked: boolean }>>((acc, date) => {
    acc[date] = { count: workoutCounts[date], marked: true };
    return acc;
  }, {});

  // Store selected date for use in DayContent
  const selectedDate = selected;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      selected={selected as any}
      className={cn("", className)}
      modifiers={{
        workoutDays: (date: Date) => {
          const dateKey = format(date, 'yyyy-MM-dd');
          return workoutDays[dateKey]?.marked ?? false;
        },
      }}
      modifiersClassNames={{
        workoutDays: 'has-workout',
      }}
      classNames={{
        months: "flex flex-col",
        month: "space-y-3",
        caption: "flex justify-between items-center px-1",
        caption_label: "text-base font-semibold text-foreground",
        nav: "flex items-center gap-1",
        nav_button: cn(
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center"
        ),
        nav_button_previous: "",
        nav_button_next: "",
        table: "w-full border-collapse",
        head_row: "flex justify-around",
        head_cell: "text-muted-foreground w-10 font-medium text-xs",
        row: "flex w-full mt-1 justify-around",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-xl"
        ),
        day: cn(
          "h-10 w-10 p-0 font-normal text-foreground rounded-xl inline-flex flex-col items-center justify-center gap-0.5 hover:bg-secondary transition-colors aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected: "bg-tmw-accent text-tmw-accent-foreground font-semibold hover:bg-tmw-accent",
        day_today: "border border-tmw-accent text-foreground",
        day_outside: "text-muted-foreground opacity-50 aria-selected:bg-tmw-accent/30 aria-selected:text-tmw-accent-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-tmw-accent/20 aria-selected:text-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-5 w-5 text-muted-foreground" />,
        IconRight: () => <ChevronRight className="h-5 w-5 text-muted-foreground" />,
        DayContent: (dayProps: ExtendedDayProps) => {
          const dateKey = format(dayProps.date, 'yyyy-MM-dd');
          const workoutDayInfo = workoutDays[dateKey];
          const isSelected = selectedDate && dayProps.date.toDateString() === selectedDate.toDateString();
          return (
            <div className="flex flex-col items-center justify-center gap-0.5">
              <span style={{ color: isSelected ? 'white' : undefined }}>{dayProps.date.getDate()}</span>
              {workoutDayInfo && (
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: isSelected ? 'white' : 'hsl(var(--tmw-accent))' }}
                />
              )}
            </div>
          );
        }
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
